import axios from "axios";
import Link from "next/link";
import { useRouter } from "next/router";
import Logo from "../../components/Icons/Logo";
import LogoIcon from "../../components/Icons/LogoIcon";
import Head from "next/head";
import { useUserContext } from "../../contexts/UserProvider";
import { sanitizeUrl } from "@braintree/sanitize-url";
import mixpanel from "mixpanel-browser";
import { LoginIllustration } from "../../components/Illustrations/LoginIllustration";
import { Button } from "ui";
import { safeLocalStorage } from "../../utils/safeLocalStorage";
import {
  Form,
  FormField,
  FormLabel,
  FormErrorMessage,
  InputFormField,
  useFormState,
} from "../../features/form";
import { FieldValues } from "react-hook-form";
import { parseErrorsToArray } from "../../utils/parseErrors";
import { identifyThirdParties } from "../../utils/thirdParties/identify";
import Loading from "../../components/Loading";
import { useRedirectToHomeIfAuthorized } from "../../hooks/useRedirectToHomeIfAuthorized";
import { getUserRole } from "../../utils/getUserRole";
type User = {
  id: number;
  is_superuser: boolean;
  is_org_user: boolean;
  is_mh_superuser: boolean;
  is_mh_user: boolean;
  email: string;
  name: string;
  organization_name: string;
  organization_id: number;
  has_completed_dashboard_onboarding: boolean;
  persona: string;
  trial_end_date: string | null;
  date_joined: string | null;
};
export default function Login() {
  const router = useRouter();

  const hasCheckedAuth = useRedirectToHomeIfAuthorized();

  const {
    setId: contextSetId,
    setRole: contextSetRole,
    setName: contextSetName,
    setEmail: contextSetEmail,
    setOrganizationName: contextSetOrgName,
    setOrganizationId: contextSetOrgId,
    setIsOnboarded: contextSetIsOnboarded,
  } = useUserContext();

  const handleSubmit = async ({ email, password }: FieldValues) => {
    return axios
      .post(`${process.env.NEXT_PUBLIC_API_BASE}/users/login/`, {
        email,
        password,
      })
      .then((response) => {
        const { returnUrl } = router.query;

        safeLocalStorage.setItem(
          "__AT",
          (response.data as any).data.token.access
        );
        safeLocalStorage.setItem(
          "__RT",
          (response.data as any).data.token.refresh
        );

        const {
          id,
          is_org_user,
          is_mh_superuser,
          email,
          name,
          organization_id,
          organization_name,
          persona,
          has_completed_dashboard_onboarding,
          trial_end_date,
          date_joined,
          is_mh_user,
        } = (response.data as { data: { user: User } }).data.user;

        mixpanel.identify(email);

        contextSetId(`${id}`);
        contextSetRole(
          getUserRole({
            is_org_user,
            is_mh_superuser,
            is_mh_user,
          })
        );
        contextSetName(name);
        contextSetEmail(email);
        contextSetOrgName(organization_name);
        contextSetOrgId(`${organization_id}`);
        contextSetIsOnboarded(has_completed_dashboard_onboarding);

        // Sanitize all navigation from urls
        const sanitizedReturnUrl = sanitizeUrl(returnUrl as string);
        const shouldRedirectToHome = sanitizedReturnUrl === "about:blank";

        identifyThirdParties({
          id,
          is_org_user,
          is_mh_superuser,
          email,
          name,
          organization_id,
          organization_name,
          persona,
          has_completed_dashboard_onboarding,
          trial_end_date,
          date_joined,
        });

        router.push(shouldRedirectToHome ? "/" : sanitizedReturnUrl);
      })
      .catch((err) => {
        console.log(err);
        const [errorMessage] = parseErrorsToArray(err);
        return { errors: { root: errorMessage || "Something went wrong." } };
      });
  };

  return (
    <div className="flex min-h-screen">
      <Head>
        <title>{process.env.NEXT_PUBLIC_APP_TITLE} | Login</title>
        <meta name="description" content="Login to dashboard" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <div></div>
      <div className="hidden w-[47%] flex-col bg-[rgba(var(--neutral-light-800))] p-8 lg:flex">
        <LogoIcon className="text-white" />
        <div className="h-full ">
          <div className="flex h-full items-center justify-center">
            <LoginIllustration />
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-center bg-foundations-subtlest px-4 py-12 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-[580px] rounded-lg border border-foundations-subtle bg-foundations-default p-8">
          {hasCheckedAuth ? (
            <>
              <div>
                <Logo />
                <h1 className="mt-6 text-2xl font-medium text-foundations-boldest">
                  Login to your account
                </h1>
              </div>

              <div className="mt-8">
                <div className="mt-6">
                  <Form onSubmit={handleSubmit} className="space-y-6">
                    <FormField
                      label="Email"
                      name="email"
                      required
                      validation="required|email"
                    >
                      <FormLabel />
                      <InputFormField
                        placeholder="Enter your email"
                        type="email"
                      />
                      <FormErrorMessage className="flex justify-end" />
                    </FormField>

                    <FormField
                      label="Password"
                      name="password"
                      required
                      validation="required"
                    >
                      <FormLabel />
                      <InputFormField
                        placeholder="Enter your password"
                        type="password"
                        autoComplete="new-password"
                      />
                      <FormErrorMessage className="flex justify-end" />
                    </FormField>

                    <div className="flex items-center justify-end">
                      <Link
                        href="/forgot-password"
                        className="text-sm text-info focus:underline focus:outline-none"
                      >
                        Forgot your password?
                      </Link>
                    </div>

                    <div>
                      <FormActionButton />

                      <FormServerErrors />
                    </div>
                  </Form>
                </div>
              </div>
            </>
          ) : (
            <Loading />
          )}
        </div>
      </div>
    </div>
  );
}

const FormActionButton = () => {
  const { isSubmitting } = useFormState();

  return (
    <Button
      variant="primary"
      type="submit"
      className="w-full"
      isLoading={isSubmitting}
    >
      Login
    </Button>
  );
};

const FormServerErrors = () => {
  const { errors } = useFormState();

  if (!errors.root?.message) return null;

  return (
    <p className="mt-2 text-center text-sm text-danger">
      {errors.root?.message}
    </p>
  );
};

Login.publicPath = true;
